import React, { useState, useEffect } from 'react';
import {
  SimpleGrid,
  Box,
  Text,
  VStack,
  Skeleton,
  Icon,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { LuStethoscope, LuSearch } from 'react-icons/lu';
import DoctorCard from './DoctorCard';
import { useDoctorList } from '../DoctorUser/hooks/useDoctorList';
import useDebounce from 'constants/helper/useDebounce';
import Pagination from '../../../components/Pagination/Pagination';

const DoctorCardsView = ({ onDoctorSelect }) => {
  const [search, setSearch] = useState('');
  const [page , setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 500);

  const {
    doctors = [],
    totalPages,
    isLoading,
    isError,
  } = useDoctorList(page, debouncedSearch);

  // Reset when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const inputBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Box py={5} px={2}>
      {/* ================= HEADER ================= */}
      <VStack align="stretch" spacing={6} mb={8}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            Practitioners Directory
          </Text>
        </Box>

        <HStack spacing={4}>
          <InputGroup maxW="lg" size="lg">
            <InputLeftElement pointerEvents="none">
              <Icon as={LuSearch} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by Name, Mobile No, ID..."
              bg={inputBg}
              borderRadius="xl"
              shadow="sm"
              border="none"
              _focus={{ shadow: 'md', ring: 2, ringColor: 'blue.400' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>

          <Badge variant="subtle" colorScheme="blue" p={2} borderRadius="md">
            {doctors.length} Records Found
          </Badge>
        </HStack>
      </VStack>

      {/* ================= GRID ================= */}
      {isLoading ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height="120px" borderRadius="2xl" />
          ))}
        </SimpleGrid>
      ) : isError ? (
        <Center h="20vh">
          <Text color="red.500">Error loading doctors.</Text>
        </Center>
      ) : doctors.length === 0 ? (
        <Center
          h="40vh"
          p={10}
          borderWidth="2px"
          borderStyle="dashed"
          borderRadius="2xl"
        >
          <VStack spacing={4}>
            <Icon as={LuStethoscope} w={12} h={12} color="gray.300" />
            <Text fontWeight="bold" color={mutedColor}>
              No doctors match your search
            </Text>
          </VStack>
        </Center>
      ) : (
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            {doctors.map((doctor) => (
              <DoctorCard
                key={doctor._id}
                doctor={doctor}
                onView={() => onDoctorSelect(doctor)}
              />
            ))}
          </SimpleGrid>
          
          {totalPages > 1 && (
            <Box mt={6}>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(newPage) => setPage(newPage)}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DoctorCardsView;
